import React, { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';

import Utilities from 'api/lib/Utilities';
import FacilityUsersProxy from 'api/facilityUsers/facilityUsersProxy';
import WorklistProxy from 'api/CoreMeasures/WorklistProxy';

import PageLayout from 'global_elements/Layouts/PageLayout';
import FlexContainer from 'global_elements/Layouts/FlexContainer';
import InlineText from 'global_elements/Text/InlineText';
import SecondaryHeader from 'global_elements/Text/SecondaryHeader';
import ProviderWorkList from 'global_elements/Layouts/DataTables/ProviderWorkList';
import SearchBar from 'global_elements/Inputs/SearchBar';

import { PageLayoutVariant } from 'global_elements/Layouts/PageLayout/variants';
import { AlignVariant, DisplayVariant, JustifyVariant } from 'global_elements/Layouts/FlexContainer/variants';
import { FontColors, FontSizes } from 'global_elements/Text/variants';
import { ProviderWorkListRow, WorkListChartDataType } from 'types/tableProps';
import { ActiveFacilityContext } from 'context/activeFacility';
import { AllRoutes } from 'constants/routes';
import { WorkListChart } from 'pages/shared/WorkCharts/workCharts';
import { MultiStandardDropdownStyles } from 'global_elements/Inputs/Dropdown/MultiSelect/styles';
import { ProgramSelectOption, SelectOption } from 'types/inputProps';

import './styles.scss';
import { UserContext } from 'context/user';
import { AccessType } from 'types/facilityUserAccount';
import Button from 'global_elements/Button';
import { ButtonVariant } from 'global_elements/Button/variants';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import MHODateTime from 'domain/dateTime/MHODateTime';
import DataListsProxy, { DataListType } from 'api/dataLists/dataListsProxy';
import LabeledSingleSelect from 'global_elements/Inputs/Dropdown/SingleSelect/LabeledSingleSelect';
import { LabeledDatePickerInput } from 'global_elements/Inputs/LabeledDatePickerInput';
import LabeledMultiSelect from 'global_elements/Inputs/Dropdown/MultiSelect/LabeledMultiSelect';
import Paragraph from 'global_elements/Text/Paragraph';
import { WorkListPatient } from 'interfaces/patients/patientData';

interface FacilityDashboardProps {
  testText: string;
  title: string;
}

/**
 * Shared dashboard page for Providers and Facility Directors.
 */
export const FacilityDashboard = (props: FacilityDashboardProps): React.JSX.Element => {
  const [loading, setLoading] = useState(true);
  const [workListLoading, setWorkListLoading] = useState(true);
  const [searchText, setSearchText] = useState<string>('');
  const [workListData, setWorkListData] = useState<ProviderWorkListRow[]>([]);
  const [workListChartData, setWorkListChartData] = useState<WorkListChartDataType>({
    facilityID: 0,
    chartData: [],
  });
  const [filteredList, setFilteredList] = useState<ProviderWorkListRow[]>([]);
  const { facility } = useContext(ActiveFacilityContext);
  const { user } = useContext(UserContext);
  const [isLoadingChart, setIsLoadingChart] = useState<boolean>(false);
  const [showProgramChart, setShowProgramChart] = useState<boolean>(true);
  const history = useHistory();
  const [serviceCodeOptions, setServiceCodeOptions] = useState<SelectOption[]>([]);
  const [selectedServiceCodes, setSelectedServiceCodes] = useState<SelectOption[]>([]);
  const [programCodeOptions, setProgramCodeOptions] = useState<ProgramSelectOption[]>([]);
  const [selectedProgram, setSelectedProgram] = useState<ProgramSelectOption | null>(null);
  const [statusOptions, setStatusOptions] = useState<SelectOption[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<SelectOption | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const getWorklist = (): void => {
    if (!facility.value.length) {
      return;
    }

    setWorkListLoading(true);
    const unitCodes = selectedServiceCodes.map((o) => o.value);
    const selectedProgramID: number | null = selectedProgram ? parseInt(selectedProgram.value, 10) : null;
    const selectedStatusID: number | null = selectedStatus ? parseInt(selectedStatus.value, 10) : null;
    FacilityUsersProxy.getProviderWorkList(facility.value, undefined, unitCodes, selectedProgramID, startDate, endDate, selectedStatusID)
      .then((resp) => {
        setLoading(false);
        if (resp.data) {
          const filteredWorkList: ProviderWorkListRow[] = resp.data
            .filter((row: WorkListPatient) => row.patientName !== null)
            .map(
              (row: WorkListPatient) => ({
                patientName: row.patientName,
                accountNumber: row.patientNumber,
                patientID: row.patientID,
                medicalRecord: row.medicalRecordNumber,
                admissionDate: row.dateAdmitted,
                program: row.programName,
                unit: row.unitCode ?? '',
                provider: row.providerName ?? '',
                patientStatus: row.lookupDesc,
                registrationPIN: row.registrationPIN ?? '',
              }) as ProviderWorkListRow,
            );
          setWorkListData(filteredWorkList);
          setFilteredList(filteredWorkList);
        } else if (resp.message) {
          setWorkListData([
            {
              patientName: resp.message,
              accountNumber: '',
              patientID: -1,
              medicalRecord: '',
              admissionDate: '',
              program: '',
              unit: '',
              provider: '',
              patientStatus: '',
              registrationPIN: '',
            },
          ]);
        }

        setWorkListLoading(false);
      })
      .catch((err) => {
        setWorkListLoading(false);
        console.log(err);
        setWorkListData([
          {
            patientName: 'An error occurred.',
            accountNumber: '',
            patientID: -1,
            medicalRecord: '',
            admissionDate: '',
            program: '',
            unit: '',
            provider: '',
            patientStatus: '',
            registrationPIN: '',
          },
        ]);
      });
  };

  useEffect(() => {
    if (user?.accessType === AccessType.CORE_MEASURES) {
      history.push('/core-measures-worklist');
    }

    DataListsProxy.getOptionListV2(DataListType.WorklistAlert)
      .then((response) => {
        if (response.data) {
          const statusList: SelectOption[] = response.data.map((x) => ({
            label: x.lookupDesc,
            value: x.lookupID.toString(),
          }));
          setStatusOptions(statusList);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  useEffect(() => {
    const facilityID = parseInt(facility.value, 10);
    if (facilityID === 0 || Number.isNaN(facilityID)) {
      return;
    }

    setSelectedProgram(null);
    setSelectedServiceCodes([]);
    setServiceCodeOptions([]);
    setProgramCodeOptions([]);

    // Load and setup the Work List Chart data based on changing facility event.
    setIsLoadingChart(true);
    FacilityUsersProxy.getWorkListChart(facilityID)
      .then((chartData) => {
        const data: WorkListChartDataType = { facilityID, chartData };
        setIsLoadingChart(false);
        setWorkListChartData(data);
      })
      .catch((error) => {
        console.log(error);
      });

    getWorklist();

    const facilityId = parseInt(facility.value, 10);
    WorklistProxy.getServiceCodes(facilityId, undefined)
      .then((results) => {
        if (results.data) {
          const options = results.data.map((x) => ({
            label: x.unitCode,
            value: x.unitCode,
          }));
          setServiceCodeOptions(options);
        }
      })
      .catch((error) => {
        console.log(error);
      });
    DataListsProxy.getPrograms(facilityId, undefined)
      .then((results) => {
        const options: ProgramSelectOption[] = results.map((x) => ({
          label: x.programName,
          value: x.programID.toString(),
        }));
        setProgramCodeOptions(options);
      })
      .catch((error) => {
        console.log(error);
      });
  }, [facility]);

  useEffect(() => {
    if (!workListLoading) {
      Utilities.setCellTitles();
    }
  }, [workListLoading]);

  // Client-side search/filter. This should ideally be a client-side search.
  const searchWorkList = (searchTerm: string): void => {
    if (searchTerm === '') {
      setFilteredList(workListData);
      return;
    }

    const tempFilteredData: ProviderWorkListRow[] = workListData.filter((row) => {
      const accountNo = String(row.accountNumber);
      const medicalRecord = String(row.medicalRecord);
      return row.patientName.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1 || accountNo.indexOf(searchTerm) !== -1 || medicalRecord.indexOf(searchTerm) !== -1;
    });
    setFilteredList(tempFilteredData);
  };

  const exportWorklistToExcel = (): void => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet 1');

    worksheet.columns = [
      { header: 'Patient Name', key: 'name', width: 32 },
      { header: 'Account Number', key: 'accountNumber', width: 15 },
      { header: 'Patient ID', key: 'patientID', width: 15 },
      { header: 'Medical Record Number', key: 'medicalRecordNumber', width: 15 },
      { header: 'Admission Date', key: 'admissionDate', width: 20 },
      { header: 'Program', key: 'program', width: 32 },
      { header: 'Unit', key: 'unit', width: 15 },
      { header: 'Provider', key: 'provider', width: 15 },
      { header: 'Patient Status', key: 'patientStatus', width: 15 },
    ];

    for (let i = 0; i < filteredList.length; i += 1) {
      worksheet.addRow({
        name: filteredList[i].patientName,
        accountNumber: filteredList[i].accountNumber,
        patientID: filteredList[i].patientID,
        medicalRecordNumber: filteredList[i].medicalRecord,
        admissionDate: new RegExp(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/).test(filteredList[i].admissionDate)
          ? new MHODateTime(filteredList[i].admissionDate).getFormattedCalendarDate()
          : filteredList[i].admissionDate,
        program: filteredList[i].program,
        unit: filteredList[i].unit,
        provider: filteredList[i].provider,
        patientStatus: filteredList[i].patientStatus,
      });
    }

    const row = worksheet.getRow(1);
    row.font = { bold: true };

    workbook.xlsx
      .writeBuffer()
      .then((buffer) => saveAs(new Blob([buffer]), `Facility_Worklist_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`))
      .catch((err) => console.log('Error writing excel export', err));
  };

  useEffect(() => {
    searchWorkList(searchText);
  }, [workListData]);

  // CVS Export button
  const CSVDownloadLink = (): React.JSX.Element => (
    <Button variant={ButtonVariant.PRIMARY} onClick={() => exportWorklistToExcel()} extraClasses="worklist-button no-print">
      <InlineText text="Export to Excel" fontColor={FontColors.BACKGROUND} fontSize={FontSizes.REGULAR} bold />
    </Button>
  );

  function handleDatepickerSelect(selectedDate: Date | null, dateType: string): void {
    if (dateType === 'startDate') {
      setStartDate(selectedDate);
      return;
    }
    setEndDate(selectedDate);
  }

  function invalidDate(myDate: any, isStartDate: boolean): string | undefined {
    const currDate = new Date();

    if (!myDate) {
      return '';
    }

    if (isStartDate && startDate && endDate && startDate > endDate) {
      return 'Start date cannot be greater than end date.';
    }

    if (endDate && !startDate) {
      return 'Start date required.';
    }

    if (myDate > currDate) {
      return 'Date cannot be in the future';
    }

    return '';
  }

  // Setup click event to view the patient
  const goToPatientDetails = (row: ProviderWorkListRow, e: React.MouseEvent<Element, MouseEvent>): void => {
    e.preventDefault();
    history.push(`${AllRoutes.PATIENT_ACCOUNTS}/${row.patientID}`);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchText(e.target.value);
    searchWorkList(e.target.value);
  };

  /**
   * Handle "View By" filter change between 'Program' and 'Service Code'.
   *
   * @param e click event
   */
  const chartFilterChanged = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setShowProgramChart(e.target.value === 'program');
  };

  if (user?.accessType === AccessType.CORE_MEASURES) {
    return (
      <PageLayout layout={PageLayoutVariant.PADDED} testText={props.testText} title={props.title}>
        Redirecting...
      </PageLayout>
    );
  }

  return (
    <PageLayout layout={PageLayoutVariant.PADDED} testText={props.testText} loadingText={loading ? 'Loading...' : undefined} pageTitle={props.title}>
      <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.CENTER} justify={JustifyVariant.START} extraClasses="facility-dashboard-content">
        <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="card-primary chart-container">
          <SecondaryHeader text="Reports" fontColor={FontColors.PRIMARY} />
          <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="card-primary">
            <div className="control-bar-top">
              <InlineText text="View By:" fontColor={FontColors.PRIMARY} fontSize={FontSizes.REGULAR} bold />
              <input type="radio" id="program-work-list-rb" name="work-list-type-rb" value="program" onChange={chartFilterChanged} checked={showProgramChart} />
              <label htmlFor="program-work-list-rb">Program</label>
              <input type="radio" id="service-work-list-rb" name="work-list-type-rb" value="service-code" onChange={chartFilterChanged} checked={!showProgramChart} />
              <label htmlFor="service-work-list-rb">Service Code</label>
            </div>
          </FlexContainer>
          <WorkListChart workListChartData={workListChartData} isLoadingChart={isLoadingChart} isProgram={showProgramChart} />
        </FlexContainer>
        <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.SPACE_BETWEEN} extraClasses="work-list-section">
          <SecondaryHeader text="Work List" fontColor={FontColors.PRIMARY} extraClasses="work-list-section__header" />
          <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.CENTER} justify={JustifyVariant.START} extraClasses="work-list-section__inputs">
            {programCodeOptions.length === 0 && (
              <LabeledSingleSelect
                // placeholder="Select a program"
                styles={MultiStandardDropdownStyles}
                options={programCodeOptions}
                defaultValue={selectedProgram ?? { label: 'Loading...', value: '' }}
                value={selectedProgram ?? { label: 'Loading...', value: '' }}
                onSelection={(option) => setSelectedProgram(option)}
                label="Select a program"
              />
            )}
            {programCodeOptions.length > 0 && (
              <LabeledSingleSelect
                // placeholder="Select a program"
                styles={MultiStandardDropdownStyles}
                options={programCodeOptions}
                defaultValue={null}
                value={selectedProgram ?? undefined}
                onSelection={(option) => setSelectedProgram(option)}
                label="Select a program"
                isClearable
              />
            )}
            {serviceCodeOptions.length === 0 && (
              <LabeledMultiSelect
                placeholder="Select or Search for Service"
                styles={MultiStandardDropdownStyles}
                options={serviceCodeOptions}
                defaultValue={selectedServiceCodes ?? [{ label: 'Loading...', value: '' }]}
                value={selectedServiceCodes ?? [{ label: 'Loading...', value: '' }]}
                onSelection={setSelectedServiceCodes}
                label="Select services"
              />
            )}
            {serviceCodeOptions.length > 0 && (
              <LabeledMultiSelect
                placeholder="Select or Search for Service"
                styles={MultiStandardDropdownStyles}
                options={serviceCodeOptions}
                defaultValue={null}
                value={selectedServiceCodes}
                onSelection={setSelectedServiceCodes}
                label="Select services"
              />
            )}
            {statusOptions.length === 0 && (
              <LabeledSingleSelect
                // placeholder="Select a program"
                styles={MultiStandardDropdownStyles}
                options={statusOptions}
                defaultValue={selectedStatus ?? { label: 'Loading...', value: '' }}
                value={selectedStatus ?? { label: 'Loading...', value: '' }}
                onSelection={(option) => setSelectedStatus(option)}
                label="Select a status"
              />
            )}
            {statusOptions.length > 0 && (
              <LabeledSingleSelect
                // placeholder="Select a program"
                styles={MultiStandardDropdownStyles}
                options={statusOptions}
                defaultValue={selectedStatus ?? null}
                value={selectedStatus ?? undefined}
                onSelection={(option) => setSelectedStatus(option)}
                label="Select a status"
                isClearable
              />
            )}
          </FlexContainer>
          <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.CENTER} justify={JustifyVariant.START} extraClasses="work-list-section__inputs">
            <label className="labeled-single-select">
              <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.CENTER} justify={JustifyVariant.START} extraClasses="labeled-single-select__header">
                <Paragraph text="Search work list" fontColor={FontColors.DARK_GRAY} fontSize={FontSizes.REGULAR} bold />
              </FlexContainer>
              <SearchBar placeholderText="Type to search work list" searchText={searchText} inputName="PatientWorklist" handleSearchChange={handleSearchChange} />
            </label>

            <LabeledDatePickerInput
              label="Admission Start Date"
              name="startDate"
              onChange={(selectedDate) => handleDatepickerSelect(selectedDate, 'startDate')}
              error={invalidDate(startDate, true)}
              testid="from-date-input"
              initialValue={startDate?.toLocaleDateString('en', { month: '2-digit', day: '2-digit', year: 'numeric' })}
              placeholder="MMDDYYYY"
            />
            <LabeledDatePickerInput
              label="Admission End Date"
              name="endDate"
              onChange={(selectedDate) => handleDatepickerSelect(selectedDate, 'endDate')}
              error={invalidDate(endDate, false)}
              testid="end-date-input"
              initialValue={endDate?.toLocaleDateString('en', { month: '2-digit', day: '2-digit', year: 'numeric' })}
              placeholder="MMDDYYYY"
            />
          </FlexContainer>
          <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.CENTER} justify={JustifyVariant.END} extraClasses="work-list-section__buttons">
            <CSVDownloadLink />
            <Button variant={ButtonVariant.PRIMARY} onClick={getWorklist} extraClasses="worklist-button">
              <InlineText text="Apply" fontColor={FontColors.WHITE} fontSize={FontSizes.REGULAR} bold />
            </Button>
          </FlexContainer>
        </FlexContainer>
        <ProviderWorkList isLoading={workListLoading} data={filteredList} onRowClicked={goToPatientDetails} onChangePage={Utilities.setCellTitles} />
      </FlexContainer>
    </PageLayout>
  );
};
